/**
 * Painel Oficina — rota do Kanban (TV) sob `/dashboard-oficina`.
 * `/dashboard-oficina/lista` redireciona ao Kanban (URL legada).
 */

import { UserRole } from '@/types';

export const PAINEL_OFICINA_BASE = '/dashboard-oficina';
/** Alias legado ainda presente em algumas permissões antigas. */
export const PAINEL_OFICINA_LEGACY_BASE = '/painel-oficina';

/** Pathname da lista (sem query); útil em permissões / módulos. */
export const PAINEL_OFICINA_LISTA_PATH = `${PAINEL_OFICINA_BASE}/lista`;
export const PAINEL_OFICINA_LEGACY_LISTA_PATH = `${PAINEL_OFICINA_LEGACY_BASE}/lista`;

/** Indica se a URL atual pertence ao painel oficina (layout full bleed). */
export function isPainelOficinaRoute(pathname: string): boolean {
  return (
    pathname === PAINEL_OFICINA_BASE ||
    pathname.startsWith(`${PAINEL_OFICINA_BASE}/`) ||
    pathname === PAINEL_OFICINA_LEGACY_BASE ||
    pathname.startsWith(`${PAINEL_OFICINA_LEGACY_BASE}/`)
  );
}

/** Hrefs cadastrados em `allowedModules` que liberam o painel (Kanban e/ou URL legada `/lista`). */
export function allowedModulesIncludePainelOficina(allowedModules: string[] | null | undefined): boolean {
  if (!Array.isArray(allowedModules) || allowedModules.length === 0) return false;
  return (
    allowedModules.includes(PAINEL_OFICINA_BASE) ||
    allowedModules.includes(PAINEL_OFICINA_LISTA_PATH) ||
    allowedModules.includes(PAINEL_OFICINA_LEGACY_BASE) ||
    allowedModules.includes(PAINEL_OFICINA_LEGACY_LISTA_PATH)
  );
}

/**
 * Acesso às rotas / API do Painel Oficina: admin sempre; gestor/operador conforme `allowedModules` quando definido.
 */
export function userCanAccessPainelOficina(
  role: UserRole,
  allowedModules?: string[] | null
): boolean {
  if (role === UserRole.ADMIN) return true;
  const restricted = Array.isArray(allowedModules) && allowedModules.length > 0;
  if (role === UserRole.GESTOR) {
    if (!restricted) return true;
    return allowedModulesIncludePainelOficina(allowedModules);
  }
  if (role === UserRole.OPERADOR) {
    if (!restricted) return false;
    return allowedModulesIncludePainelOficina(allowedModules);
  }
  return false;
}

/** Se a home após login deve ser o Kanban da oficina (sem acesso ao dashboard principal). */
export function userHomeIsPainelOficina(allowedModules: string[] | null | undefined): boolean {
  if (!Array.isArray(allowedModules) || allowedModules.length === 0) return false;
  if (allowedModules.includes('/dashboard')) return false;
  const painelSet = new Set([
    PAINEL_OFICINA_BASE,
    PAINEL_OFICINA_LISTA_PATH,
    PAINEL_OFICINA_LEGACY_BASE,
    PAINEL_OFICINA_LEGACY_LISTA_PATH,
  ]);
  const hasPainel = allowedModulesIncludePainelOficina(allowedModules);
  if (!hasPainel) return false;
  // Se o usuário só tem variações do módulo Oficina, entra direto no painel.
  return allowedModules.every((href) => painelSet.has(href));
}

export type PainelOficinaPaths = {
  companyId: string | undefined;
  kanbanHref: string;
  indicadoresHref: string;
  kanbanLocation: { pathname: string; search: string };
};

/**
 * Monta paths do Painel Oficina com `companyId` opcional na query.
 */
export function painelOficina(companyId?: string | null): PainelOficinaPaths {
  const search = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return {
    companyId: companyId ?? undefined,
    kanbanHref: `${PAINEL_OFICINA_BASE}${search}`,
    indicadoresHref: `${PAINEL_OFICINA_BASE}/indicadores${search}`,
    kanbanLocation: { pathname: PAINEL_OFICINA_BASE, search },
  };
}
